import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import DataTable from '../components/DataTable';
import Form from '../components/Form';
import apiService from '../services/api';
import { FormField, Metadata, TableColumn } from '../types/api';

const MetadataPage: React.FC = () => {
  const [selectedKey, setSelectedKey] = useState<string>('');
  const [metadata, setMetadata] = useState<Metadata | null>(null);
  const [allMetadata, setAllMetadata] = useState<Metadata[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isCreate, setIsCreate] = useState<boolean>(false);
  const [formLoading, setFormLoading] = useState<boolean>(false);

  // Since swagger does not define list-all endpoint, we provide a simple UI to fetch by key, create/update, and delete.

  const fetchByKey = async (key: string) => {
    if (!key) return;
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getMetadataByKey(key);
      setMetadata(data);
      setIsCreate(false);
    } catch (e: any) {
      setMetadata(null);
      setError(e.message || 'Failed to fetch metadata');
    } finally {
      setLoading(false);
    }
  };

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const list = await apiService.getAllMetadata();
      setAllMetadata(list);
    } catch (e: any) {
      setError(e.message || 'Failed to fetch metadata list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleCreate = async (formData: Record<string, any>) => {
    try {
      setFormLoading(true);
      setError(null);
      const created = await apiService.createMetadata(formData.key, parseJsonIfPossible(formData.data));
      setMetadata(created);
      setSelectedKey(created.key);
      setIsCreate(false);
      setSuccess('Metadata created');
      clearSuccessLater();
      fetchAll();
    } catch (e: any) {
      setError(e.message || 'Failed to create metadata');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedKey) return;
    if (!window.confirm(`Delete metadata for key "${selectedKey}"?`)) return;
    try {
      setLoading(true);
      setError(null);
      await apiService.deleteMetadata(selectedKey);
      setMetadata(null);
      setSelectedKey('');
      setSuccess('Metadata deleted');
      clearSuccessLater();
      fetchAll();
    } catch (e: any) {
      setError(e.message || 'Failed to delete metadata');
    } finally {
      setLoading(false);
    }
  };

  const clearSuccessLater = () => {
    setTimeout(() => setSuccess(null), 1500);
  };

  const parseJsonIfPossible = (input: any) => {
    if (typeof input !== 'string') return input;
    try {
      return JSON.parse(input);
    } catch {
      return input; // allow raw string
    }
  };

  const createFields: FormField[] = useMemo(() => [
    { name: 'key', label: 'Key', type: 'text', required: true },
    { name: 'data', label: 'Data (JSON or plain text)', type: 'textarea', required: true },
  ], []);

  useMemo(() => {
    if (!metadata) return '';
    try {
      return typeof metadata.data === 'string' ? metadata.data : JSON.stringify(metadata.data, null, 2);
    } catch {
      return String(metadata.data);
    }
  }, [metadata]);

  const allColumns: TableColumn<Metadata>[] = useMemo(() => [
    {
      key: 'key',
      label: 'Key',
      sortable: true,
    },
    {
      key: 'data',
      label: 'Data',
      render: (value: any) => {
        try {
          const str = typeof value === 'string' ? value : JSON.stringify(value);
          return str.length > 100 ? str.slice(0, 100) + '…' : str;
        } catch {
          return String(value);
        }
      }
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      render: (value: any) => value ? new Date(value).toLocaleString() : '-',
      sortable: true,
    },
    {
      key: 'createdAt',
      label: 'Created At',
      render: (value: any) => value ? new Date(value).toLocaleString() : '-',
      sortable: true,
    },
  ], []);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Metadata</h2>
          <p className="text-gray-600">Create, read, update, and delete metadata by key</p>
        </div>
      </div>

      {error && (
        <div className="error mb-4">{error}</div>
      )}

      {success && (
        <div className="success mb-4">{success}</div>
      )}

      <div className="card mb-6">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="card-title">Find by Key</h3>
            <button className="btn btn-secondary" onClick={fetchAll} disabled={loading}>Refresh All</button>
          </div>
        </div>
        <div className="card-content">
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Key</label>
              <input
                type="text"
                className="input"
                value={selectedKey}
                onChange={(e) => setSelectedKey(e.target.value)}
                placeholder="e.g., app.config"
              />
            </div>
            <button className="btn btn-primary" onClick={() => fetchByKey(selectedKey)} disabled={!selectedKey || loading}>
              Fetch
            </button>
            <button className="btn btn-secondary" onClick={() => { setIsCreate(true); setMetadata(null); setError(null); setSuccess(null); }}>
              <Plus className="inline-block mr-1" size={16} /> New
            </button>
            {metadata && (
              <button className="btn btn-danger" onClick={handleDelete}>
                <Trash2 className="inline-block mr-1" size={16} /> Delete
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-header">
          <h3 className="card-title">All Metadata</h3>
        </div>
        <div className="card-content">
          <DataTable
            data={allMetadata}
            columns={allColumns}
            loading={loading}
            error={error}
            itemsPerPage={15}
            onRowClick={(row) => {
              setSelectedKey(row.key);
              setMetadata(row);
              setIsCreate(false);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </div>
      </div>

      {isCreate && (
        <div className="card mb-6">
          <div className="card-header">
            <h3 className="card-title">Create Metadata</h3>
          </div>
          <div className="card-content">
            <Form
              fields={createFields}
              onSubmit={handleCreate}
              loading={formLoading}
              initialData={{ key: selectedKey, data: '' }}
              submitText="Create"
            />
          </div>
        </div>
      )}

      {/* Edit functionality removed intentionally */}
    </div>
  );
};

export default MetadataPage;


